/**
 * Messaging WebSocket Service
 *
 * Uses ActionCable (Rails WebSocket protocol) for real-time messaging
 * Compatible with Chatwoot's ActionCable-based WebSocket implementation
 * Goes through messaging hub proxy for unified authentication
 */

import { createConsumer, Consumer, Subscription } from "@rails/actioncable";
import config from "@/config";
import { getUserToken } from "@/services/storage-data";
import {
  DMMessage,
  DMPresenceData,
} from "@/widgets/frontdesk/providers/internal/types";
import { toSecureWebSocketCableUrl } from "./to-secure-websocket-url";

// Use DMMessage as Message for compatibility
export type Message = DMMessage;
export type PresenceData = DMPresenceData;

// Define messaging types
export interface SendMessageData {
  conversationId: string;
  content: string;
  recipientId?: string;
  tempId?: string;
  messageId?: string;
  metadata?: {
    type: 'text' | 'file' | 'image';
  };
}

export interface JoinChannelData {
  conversationId: string;
}

export interface TypingData {
  conversationId: string;
}

export interface MarkAsReadData {
  messageId: string;
}

export interface UpdateStatusData {
  status: 'online' | 'away' | 'busy' | 'offline';
}

export interface GetPresenceData {
  userIds?: string[];
}

export interface SocketResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: any;
  messageId?: string;
  conversationId?: string;
  onlineUsers?: string[];
  presence?: Record<string, PresenceData>;
}

export interface TypingIndicator {
  userId: string;
  userEmail: string;
  userName: string;
  conversationId: string;
  isTyping: boolean;
}

export interface MessageConfirmedData {
  tempId: string;
  messageId: string;
  conversationId: string;
  timestamp?: string;
}

// Event handler types
export type MessageEventHandler = (message: Message) => void;
export type PresenceEventHandler = (data: any) => void;
export type TypingEventHandler = (data: TypingIndicator) => void;
export type ConnectionEventHandler = (connected: boolean) => void;
export type ErrorEventHandler = (error: string) => void;
export type MessageConfirmedHandler = (data: MessageConfirmedData) => void;

/**
 * Connection State
 */
export enum ConnectionState {
  DISCONNECTED = "disconnected",
  CONNECTING = "connecting",
  CONNECTED = "connected",
  ERROR = "error",
}

/**
 * Messaging Service using ActionCable
 *
 * This service provides Socket.IO-compatible API but uses ActionCable
 * under the hood for compatibility with Chatwoot's WebSocket server
 */
export class MessagingService {
  private consumer: Consumer | null = null;
  private subscription: Subscription | null = null;
  private serverUrl: string;
  private isConnecting: boolean = false;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;

  // Event handlers - maintain compatibility with Socket.IO style
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private connectionHandlers: Set<ConnectionEventHandler> = new Set();
  private errorHandlers: Set<ErrorEventHandler> = new Set();

  // Pending callbacks for request/response pattern
  private pendingCallbacks: Map<string, (response: SocketResponse) => void> = new Map();
  private callbackCounter: number = 0;

  constructor() {
    this.serverUrl = config.messaging_server || config.api_server;
  }

  /**
   * Generate a persistent session ID for better connection tracking
   */
  private generateSessionId(): string {
    const existingSessionId = localStorage.getItem('messaging_session_id');
    if (existingSessionId) {
      return existingSessionId;
    }

    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('messaging_session_id', sessionId);
    return sessionId;
  }

  /**
   * Connect to messaging service using ActionCable
   */
  connect(authToken?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.consumer && this.connectionState === ConnectionState.CONNECTED) {
        resolve(this.consumer);
        return;
      }

      if (this.isConnecting) {
        reject(new Error("Connection already in progress"));
        return;
      }

      this.isConnecting = true;
      this.clearHeartbeat();

      const token = authToken || getUserToken();
      console.log("Messaging Service - Auth Token:", token ? "Present" : "Missing");

      if (!token) {
        this.isConnecting = false;
        console.error("Messaging Service - No authentication token available");
        reject(new Error("No authentication token available"));
        return;
      }

      // Construct WebSocket URL with token
      const wsUrl = toSecureWebSocketCableUrl(this.serverUrl, token);
      console.log("Messaging Service - Connecting via ActionCable to:", wsUrl.replace(/token=[^&]*/, "token=***"));

      try {
        // Create ActionCable consumer
        this.consumer = createConsumer(wsUrl);

        // Subscribe to MessagingChannel (or appropriate channel name)
        this.subscription = this.consumer.subscriptions.create(
          {
            channel: "MessagingChannel", // This may need adjustment based on backend
            session_id: this.generateSessionId(),
          },
          {
            connected: () => {
              console.log("Connected to messaging service via ActionCable");
              this.isConnecting = false;
              this.connectionState = ConnectionState.CONNECTED;
              this.startHeartbeat();

              // Notify connection handlers
              this.connectionHandlers.forEach(handler => handler(true));

              // Emit Socket.IO style 'connect' event
              this.emitEvent("connect", {});

              resolve(this.consumer);
            },

            disconnected: () => {
              console.log("Disconnected from messaging service");
              this.clearHeartbeat();
              this.isConnecting = false;
              this.connectionState = ConnectionState.DISCONNECTED;

              // Notify connection handlers
              this.connectionHandlers.forEach(handler => handler(false));

              // Emit Socket.IO style 'disconnect' event
              this.emitEvent("disconnect", { reason: "transport close" });
            },

            rejected: () => {
              const error = "Connection rejected (invalid credentials or permissions)";
              console.error(error);
              this.isConnecting = false;
              this.connectionState = ConnectionState.ERROR;

              // Notify error handlers
              this.errorHandlers.forEach(handler => handler(error));

              reject(new Error(error));
            },

            received: (data: any) => {
              // Handle incoming messages from ActionCable
              this.handleReceivedData(data);
            },
          }
        );

        // Add connection timeout
        setTimeout(() => {
          if (this.connectionState === ConnectionState.DISCONNECTED && this.isConnecting) {
            this.isConnecting = false;
            this.connectionState = ConnectionState.ERROR;
            const error = "Connection timeout";
            this.errorHandlers.forEach(handler => handler(error));
            reject(new Error(error));
          }
        }, 20000); // 20 second timeout

      } catch (error: any) {
        this.isConnecting = false;
        this.connectionState = ConnectionState.ERROR;
        console.error("Failed to create ActionCable consumer:", error);

        // Notify error handlers
        const errorMessage = error?.message || "Failed to connect";
        this.errorHandlers.forEach(handler => handler(errorMessage));

        reject(error);
      }
    });
  }

  /**
   * Handle received data from ActionCable
   */
  private handleReceivedData(data: any): void {
    // Check if it's a callback response
    if (data.callbackId && this.pendingCallbacks.has(data.callbackId)) {
      const callback = this.pendingCallbacks.get(data.callbackId);
      this.pendingCallbacks.delete(data.callbackId);
      if (callback) {
        callback(data.response || data);
      }
      return;
    }

    // Handle different event types
    if (data.event) {
      this.emitEvent(data.event, data.data || data);
    } else if (data.type) {
      // Map type to event name for compatibility
      const eventMap: Record<string, string> = {
        'message': 'newMessage',
        'direct_message': 'directMessage',
        'typing_on': 'userTyping',
        'typing_off': 'userTyping',
        'presence_update': 'statusChanged',
        'message_read': 'messageRead',
        'message_confirmed': 'messageConfirmed',
        'user_online': 'userOnline',
        'user_offline': 'userOffline',
      };

      const eventName = eventMap[data.type] || data.type;
      this.emitEvent(eventName, data);
    } else {
      // Default handling for unstructured data
      this.emitEvent('data', data);
    }
  }

  /**
   * Emit event to registered handlers
   */
  private emitEvent(event: string, data: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Send data via ActionCable subscription
   */
  private send(action: string, data: any, callback?: (response: SocketResponse) => void): void {
    if (!this.subscription) {
      console.error("Not connected to messaging service");
      if (callback) {
        callback({ success: false, error: "Not connected" });
      }
      return;
    }

    const payload: any = {
      action,
      data,
    };

    // Add callback ID if callback provided
    if (callback) {
      const callbackId = `callback_${++this.callbackCounter}`;
      payload.callbackId = callbackId;
      this.pendingCallbacks.set(callbackId, callback);

      // Timeout for callback after 30 seconds
      setTimeout(() => {
        if (this.pendingCallbacks.has(callbackId)) {
          this.pendingCallbacks.delete(callbackId);
          callback({ success: false, error: "Request timeout" });
        }
      }, 30000);
    }

    // Send via ActionCable
    this.subscription.perform(action, payload);
  }

  disconnect(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = null;
    }

    this.isConnecting = false;
    this.connectionState = ConnectionState.DISCONNECTED;
    this.clearHeartbeat();
    this.pendingCallbacks.clear();

    // Notify connection handlers
    this.connectionHandlers.forEach(handler => handler(false));
  }

  private startHeartbeat(): void {
    if (!this.subscription) return;

    this.clearHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      if (this.subscription) {
        this.subscription.perform("heartbeat", {});
      } else {
        this.clearHeartbeat();
      }
    }, 30000);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  isConnected(): boolean {
    return this.connectionState === ConnectionState.CONNECTED;
  }

  getSocket(): any {
    // Return consumer for compatibility
    return this.consumer;
  }

  // Message operations
  sendMessage(data: SendMessageData): void {
    this.send("sendMessage", data);
  }

  sendMessageWithConfirmation(data: SendMessageData): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("sendMessage", data, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to send message"));
        }
      });
    });
  }

  // Channel operations
  joinChannel(data: JoinChannelData): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("joinChannel", data, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to join channel"));
        }
      });
    });
  }

  leaveChannel(data: JoinChannelData): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("leaveChannel", data, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to leave channel"));
        }
      });
    });
  }

  // Typing indicators
  startTyping(data: TypingData): void {
    this.send("typingStart", data);
  }

  stopTyping(data: TypingData): void {
    this.send("typingStop", data);
  }

  // Read receipts
  markAsRead(data: MarkAsReadData): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("markAsRead", data, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to mark as read"));
        }
      });
    });
  }

  // Presence operations
  updateStatus(data: UpdateStatusData): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("updateStatus", data, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to update status"));
        }
      });
    });
  }

  getPresence(data: GetPresenceData = {}): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("getPresence", data, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to get presence"));
        }
      });
    });
  }

  // HTTP REST endpoint to fetch channel message history (keep for backward compatibility)
  async fetchChannelMessages(
    channelId: string,
    options: { limit?: number; offset?: number } = {}
  ): Promise<{ messages: Message[]; hasMore?: boolean; total?: number }> {
    const { limit = 50, offset = 0 } = options;
    const token = getUserToken();

    if (!token) {
      throw new Error("No authentication token available");
    }

    const params = new URLSearchParams();
    if (limit) params.append("limit", limit.toString());
    if (offset) params.append("offset", offset.toString());

    const queryString = params.toString() ? `?${params.toString()}` : "";
    const url = `${this.serverUrl}/messages/channel/${encodeURIComponent(channelId)}${queryString}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle various response formats
      if (data && Array.isArray(data)) {
        return {
          messages: data,
          hasMore: false,
          total: data.length,
        };
      } else if (data.success !== false && data.messages && Array.isArray(data.messages)) {
        return {
          messages: data.messages,
          hasMore: data.hasMore || false,
          total: data.total || data.messages.length,
        };
      } else if (data.success && data.data && Array.isArray(data.data)) {
        return {
          messages: data.data,
          hasMore: data.hasMore || false,
          total: data.total || data.data.length,
        };
      } else {
        throw new Error(data.message || data.error || "Failed to fetch channel messages");
      }
    } catch (error: any) {
      console.error("Failed to fetch channel messages:", error);
      throw error;
    }
  }

  // Socket.IO compatible event listener methods
  private on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  private off(event: string, handler?: Function): void {
    if (handler) {
      const handlers = this.eventHandlers.get(event);
      if (handlers) {
        handlers.delete(handler);
      }
    } else {
      this.eventHandlers.delete(event);
    }
  }

  // Event listeners (Socket.IO compatible API)
  onNewMessage(handler: MessageEventHandler): void {
    this.on("newMessage", handler);
  }

  onDirectMessage(handler: MessageEventHandler): void {
    this.on("directMessage", handler);
  }

  onUserOnline(handler: PresenceEventHandler): void {
    this.on("userOnline", handler);
  }

  onUserOffline(handler: PresenceEventHandler): void {
    this.on("userOffline", handler);
  }

  onUserTyping(handler: TypingEventHandler): void {
    this.on("userTyping", handler);
  }

  onStatusChanged(handler: PresenceEventHandler): void {
    this.on("statusChanged", handler);
  }

  onMessageRead(handler: (data: { messageId: string; readBy: string; readAt: string }) => void): void {
    this.on("messageRead", handler);
  }

  onMessageConfirmed(handler: MessageConfirmedHandler): void {
    this.on("messageConfirmed", handler);
  }

  onConnect(handler: ConnectionEventHandler): void {
    this.connectionHandlers.add(handler);
    this.on("connect", () => handler(true));
  }

  onDisconnect(handler: ConnectionEventHandler): void {
    this.connectionHandlers.add(handler);
    this.on("disconnect", () => handler(false));
  }

  onError(handler: ErrorEventHandler): void {
    this.errorHandlers.add(handler);
    this.on("connect_error", (error: any) => handler(error?.message || "Connection error"));
    this.on("exception", (error: any) => handler(error?.message || "Socket exception"));
  }

  // Channel management methods (maintain compatibility)
  createChannel(data: { name: string; description?: string; isPrivate?: boolean }): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("createChannel", data, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to create channel"));
        }
      });
    });
  }

  getChannels(): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("getChannels", {}, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to get channels"));
        }
      });
    });
  }

  archiveConversation(channelId: string): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("archiveConversation", { channelId }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to archive conversation"));
        }
      });
    });
  }

  unarchiveConversation(channelId: string): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("unarchiveConversation", { channelId }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to unarchive conversation"));
        }
      });
    });
  }

  deleteConversation(channelId: string): Promise<SocketResponse> {
    return new Promise((resolve, reject) => {
      this.send("deleteConversation", { channelId }, (response) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || "Failed to delete conversation"));
        }
      });
    });
  }

  // Remove event listeners
  removeAllListeners(): void {
    this.eventHandlers.clear();
    this.connectionHandlers.clear();
    this.errorHandlers.clear();
  }

  removeListener(event: string, handler?: Function): void {
    this.off(event, handler);
  }
}

// Singleton instance
export const messagingService = new MessagingService();